'use client';

import { useState } from 'react';
import { cn } from '@/lib/utils';
import Image from 'next/image';

/**
 * A wrapper around the HTML <img> tag (or Next.js Image if adapted) that provides
 * a smooth fade-in effect and a skeleton loader while the image is downloading.
 * 
 * @param {string} src - Image source URL
 * @param {string} alt - Alt text
 * @param {string} className - Classes for the container/image
 * @param {boolean} fill - If true, image fills the parent container (absolute positioned)
 * @param {object} props - Additional props passed to the img element
 */
export function BlurImage({ src, alt, className, fill = false, priority = false, sizes, ...props }) {
    const [isLoading, setIsLoading] = useState(true);

    // If src is an object (imported asset), it has .src property
    const imageSource = typeof src === 'object' ? src.src || src : src;

    return (
        <div className={cn(
            "overflow-hidden relative",
            fill ? "w-full h-full" : "",
            isLoading && "bg-gray-200 animate-pulse",
            className?.includes("rounded") ? className.match(/rounded-[^ ]*/)?.[0] : ""
        )}>
            <Image
                src={imageSource}
                alt={alt || ''}
                fill={fill}
                priority={priority}
                sizes={sizes || (fill ? "100vw" : undefined)}
                className={cn(
                    "transition-opacity duration-700 ease-in-out block object-cover",
                    isLoading ? "opacity-0" : "opacity-100",
                    className
                )}
                onLoad={() => setIsLoading(false)}
                {...props}
            />
        </div>
    );
}


// Also export a version that uses next/image if needed, but for now we stick to <img> for compatibility with existing code
